import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, MapPin, Ruler, FileCheck2, User, Camera, ArrowRight, Info } from 'lucide-react';

// EvidenceAlertVisual: Full-width evidence-first alert visual with a
// baseline vs latest observation view. Both scenes are synthesised as SVG
// so they always reflect the actual product concept (topographic slope
// view with a change polygon), not random stock photography.
export function EvidenceAlertVisual() {
  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="evidence-alert-visual">
      <div className="lg:col-span-2">
        <Card className="overflow-hidden border-border">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-500" />
              <div className="font-semibold text-[14px]">Probable terrain disturbance near access road</div>
            </div>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-amber-500/40 text-amber-700 dark:text-amber-400">Simulated demonstration scenario</Badge>
          </div>
          <div className="grid grid-cols-2">
            <ImagePane label="Baseline observation" date="12 Apr 2026" />
            <ImagePane label="Latest observation" date="14 Jul 2026" withChangePolygon />
          </div>
          <div className="px-4 py-3 border-t border-border text-[11px] text-muted-foreground flex items-start gap-2">
            <Info size={12} className="mt-0.5 shrink-0" />
            <span>
              Simulated demonstration scenario. This does not represent an actual incident at the displayed location.
            </span>
          </div>
        </Card>
      </div>

      <div>
        <Card className="p-5 border-border h-full">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Evidence summary</div>
          <ul className="mt-4 space-y-3 text-sm">
            <EvidenceRow Icon={MapPin} label="Location" value="30.372°N, 78.462°E" />
            <EvidenceRow Icon={Ruler} label="Affected area" value="~ 4,180 m²" />
            <EvidenceRow Icon={Ruler} label="Distance from access road" value="120 m" />
            <EvidenceRow Icon={Ruler} label="Distance from core asset" value="1,842 m" />
            <EvidenceRow Icon={Info} label="AI confidence" value="83%" />
            <EvidenceRow Icon={AlertTriangle} label="Risk level" value="High" tone="warn" />
            <EvidenceRow Icon={FileCheck2} label="Evidence quality" value="Moderate" />
            <EvidenceRow Icon={User} label="Analyst validation" value="Under Analyst Review" />
            <EvidenceRow Icon={ArrowRight} label="Recommended action" value="Field verification" />
            <EvidenceRow Icon={Camera} label="Field verification" value="Pending" />
          </ul>
          <div className="mt-5 pt-4 border-t border-border text-xs text-muted-foreground italic leading-relaxed">
            Probable terrain disturbance identified 120 metres from the primary access road. Analyst review completed. Field verification recommended.
          </div>
        </Card>
      </div>
    </div>
  );
}

function ImagePane({ label, date, withChangePolygon }) {
  return (
    <div className="relative bg-secondary">
      <SyntheticTerrain withPolygon={withChangePolygon} />
      <div className="absolute top-3 left-3 rounded-md bg-background/85 backdrop-blur px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className="absolute top-3 right-3 rounded-md bg-background/85 backdrop-blur px-2 py-1 text-[10px] font-mono text-muted-foreground">
        {date}
      </div>
      {withChangePolygon && (
        <div className="absolute bottom-3 left-3 rounded-md bg-background/85 backdrop-blur px-2 py-1 text-[10px]">
          <span className="text-red-600">Change polygon</span> · <span className="font-mono">4,180 m²</span>
        </div>
      )}
    </div>
  );
}

function SyntheticTerrain({ withPolygon }) {
  return (
    <svg viewBox="0 0 400 280" xmlns="http://www.w3.org/2000/svg" className="w-full h-[280px] block" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id={`ter-bg-${withPolygon ? 'a' : 'b'}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3f5232" />
          <stop offset="55%" stopColor="#5c6a3d" />
          <stop offset="100%" stopColor="#3a4a2a" />
        </linearGradient>
      </defs>
      <rect width="400" height="280" fill={`url(#ter-bg-${withPolygon ? 'a' : 'b'})`} />
      {/* Contour lines */}
      {[0, 30, 60, 90, 120, 150, 180, 210, 240].map(y => (
        <path key={y} d={`M0 ${y + 20} Q100 ${y - 10} 200 ${y + 15} T400 ${y + 20}`} stroke="rgba(255,255,255,0.08)" fill="none" strokeWidth="1" />
      ))}
      {/* Access road */}
      <path d="M20 220 Q100 210 180 200 Q260 190 340 175" stroke="rgba(240,240,220,0.7)" strokeWidth="3" fill="none" strokeDasharray="6 4" />
      <text x="22" y="235" fontSize="9" fill="rgba(255,255,255,0.75)" fontFamily="monospace">ACCESS ROAD</text>
      {/* Vegetation / tree dots */}
      {[[50,80],[110,60],[80,120],[250,90],[320,50],[290,140],[340,110],[150,40],[180,90],[210,50],[70,180],[125,150]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="5" fill="rgba(30,60,20,0.7)" />
      ))}
      {/* Grid overlay */}
      <path d="M0 0 L400 0 M0 70 L400 70 M0 140 L400 140 M0 210 L400 210 M0 280 L400 280" stroke="rgba(31,182,166,0.08)" strokeWidth="0.5" />
      <path d="M0 0 L0 280 M80 0 L80 280 M160 0 L160 280 M240 0 L240 280 M320 0 L320 280 M400 0 L400 280" stroke="rgba(31,182,166,0.08)" strokeWidth="0.5" />
      {/* Change polygon (only on "after") */}
      {withPolygon && (
        <>
          <path d="M195 165 L250 155 L268 180 L255 210 L220 218 L195 200 Z" fill="rgba(239,68,68,0.28)" stroke="#ef4444" strokeWidth="1.5" />
          {/* Fresh soil texture */}
          <path d="M200 175 L260 165 M205 190 L262 178 M210 205 L258 195" stroke="rgba(155,80,40,0.55)" strokeWidth="1.2" />
        </>
      )}
      {/* North indicator */}
      <g transform="translate(370, 250)" fill="rgba(255,255,255,0.75)" fontFamily="monospace" fontSize="9">
        <circle r="10" fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" />
        <path d="M0 -8 L2 3 L0 0 L-2 3 Z" fill="#fff" />
        <text x="-3" y="-11" fontSize="7">N</text>
      </g>
    </svg>
  );
}

function EvidenceRow({ Icon, label, value, tone }) {
  const valueClass = tone === 'warn' ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-foreground font-medium';
  return (
    <li className="flex items-center gap-2">
      <Icon size={12} className="text-muted-foreground shrink-0" />
      <span className="text-muted-foreground flex-1">{label}</span>
      <span className={valueClass}>{value}</span>
    </li>
  );
}
